import { useEffect, useState } from "react";
import { api, inr, dateFmt, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { PlusCircle, Trash2, IndianRupee } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

function defaultPaymentForm() {
  const now = new Date();
  const yyyymm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  return {
    cadence: "monthly",
    period: yyyymm,
    amount: "",
    payment_date: todayISO(),
    cin: "",
    notes: "",
  };
}

export default function GST() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultPaymentForm());
  const [saving, setSaving] = useState(false);

  const load = async (p) => {
    const url = p ? `/tax/gst-summary?period=${p}` : "/tax/gst-summary";
    const r = await api.get(url);
    setData(r.data);
  };

  useEffect(() => { load(""); }, []);

  const setPeriodAndFetch = (p) => { setPeriod(p); load(p); };

  const now = new Date();
  const months = Array.from({ length: 6 }, (_, idx) => {
    const d = new Date(now.getFullYear(), now.getMonth() - idx, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const openPaymentModal = () => {
    setForm(defaultPaymentForm());
    setOpen(true);
  };

  const savePayment = async () => {
    if (!form.period || !form.amount || !form.payment_date) {
      toast.error("Period, amount and date are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/tax/gst-payments", {
        period: form.period.trim(),
        amount: Number(form.amount),
        payment_date: form.payment_date,
        cin: form.cin?.trim() || null,
        notes: form.notes?.trim() || null,
        cadence: form.cadence,
      });
      toast.success("GST payment recorded");
      setOpen(false);
      await load(period);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save payment");
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async (id) => {
    if (!window.confirm("Delete this payment record?")) return;
    try {
      await api.delete(`/tax/gst-payments/${id}`);
      toast.success("Payment deleted");
      await load(period);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  // Build quarter options for current FY (Apr-Mar)
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const quarters = [1, 2, 3, 4].map((q) => `${fyStartYear}-Q${q}`);

  return (
    <div className="space-y-8" data-testid="gst-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="tc-overline mb-2">Goods &amp; Services Tax</p>
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">GST liability</h1>
          <p className="text-sm text-[#5a5a53] mt-2 max-w-xl">
            Track what you owe, what you've paid, and what's still pending. Use this view to file GSTR-1 (sales) and GSTR-3B (summary).
          </p>
        </div>
        <Button data-testid="gst-record-payment-btn" onClick={openPaymentModal}
          className="bg-[#d95d39] hover:bg-[#c14e2c] text-white">
          <PlusCircle className="w-4 h-4 mr-2" /> Record GST payment
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="gst-period-filters">
        <button onClick={() => setPeriodAndFetch("")} data-testid="gst-period-all"
          className={`px-3 py-1.5 text-xs rounded-md border ${period === "" ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "border-[#e5e5e0] text-[#5a5a53] hover:bg-white"}`}>
          All time
        </button>
        {months.map((m) => (
          <button key={m} onClick={() => setPeriodAndFetch(m)} data-testid={`gst-period-${m}`}
            className={`px-3 py-1.5 text-xs rounded-md border font-mono ${period === m ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "border-[#e5e5e0] text-[#5a5a53] hover:bg-white"}`}>
            {m}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="text-sm text-[#5a5a53]">Loading…</div>
      ) : (
        <>
          {/* Headline KPI strip — what owed / paid / pending */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#e5e5e0] border border-[#e5e5e0] rounded-md overflow-hidden">
            <Stat label="Output GST" value={inr(data.total_output)} sub="On sales" />
            <Stat label="– Input credit" value={`−${inr(data.input_credit)}`} sub="From expenses" accent="#4a6b53" />
            <Stat label="Net payable" value={inr(data.net_payable)} sub="After credits" accent="#0a0a0a" testid="gst-net-payable" />
            <Stat label="Paid" value={inr(data.total_paid || 0)} sub="Recorded by you" accent="#4a6b53" testid="gst-total-paid" />
          </div>

          <div className="tc-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            <div className="min-w-0">
              <p className="tc-overline">Net payable {period && `(${period})`}</p>
              <p className="font-['Outfit'] text-2xl md:text-3xl font-bold mt-2 break-words">{inr(data.net_payable)}</p>
            </div>
            <div className="min-w-0">
              <p className="tc-overline">– Paid</p>
              <p className="font-['Outfit'] text-2xl md:text-3xl font-bold mt-2 text-[#4a6b53] break-words">−{inr(data.total_paid || 0)}</p>
            </div>
            <div className={`rounded-md p-6 -m-2 min-w-0 ${data.pending > 0 ? "bg-[#0a0a0a] text-white" : "bg-[#4a6b53] text-white"}`}>
              <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#d95d39]">Pending to pay</p>
              <p className="font-['Outfit'] text-3xl md:text-4xl font-bold mt-2 break-words" data-testid="gst-pending">
                {inr(data.pending || 0)}
              </p>
            </div>
          </div>

          {/* Component split for reference */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-[#e5e5e0] border border-[#e5e5e0] rounded-md overflow-hidden">
            <Stat label="CGST" value={inr(data.output_cgst)} small />
            <Stat label="SGST" value={inr(data.output_sgst)} small />
            <Stat label="IGST" value={inr(data.output_igst)} small />
          </div>

          {/* Month-wise table now showing payable AND paid */}
          <div className="tc-card overflow-hidden">
            <div className="p-6 border-b border-[#e5e5e0]">
              <p className="tc-overline">Month-wise breakup (GSTR-1 ready)</p>
              <h2 className="font-['Outfit'] text-2xl font-medium mt-1">Taxable supplies vs payments</h2>
            </div>
            {(!data.by_month || data.by_month.length === 0) ? (
              <div className="p-12 text-sm text-[#5a5a53] text-center">No invoices in this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]" data-testid="gst-by-month">
                  <thead className="bg-[#f4f1eb] text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">
                    <tr>
                      <th className="text-left px-6 py-3 font-bold">Month</th>
                      <th className="text-right px-6 py-3 font-bold">Taxable value</th>
                      <th className="text-right px-6 py-3 font-bold">GST</th>
                      <th className="text-right px-6 py-3 font-bold">Paid</th>
                      <th className="text-right px-6 py-3 font-bold">Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_month.map((b) => (
                      <tr key={b.month} className="border-t border-[#e5e5e0]" data-testid={`gst-row-${b.month}`}>
                        <td className="px-6 py-3 font-mono">{b.month}</td>
                        <td className="px-6 py-3 text-right font-mono">{inr(b.taxable)}</td>
                        <td className="px-6 py-3 text-right font-mono text-[#d95d39] font-bold">{inr(b.gst)}</td>
                        <td className="px-6 py-3 text-right font-mono text-[#4a6b53]">{inr(b.paid || 0)}</td>
                        <td className={`px-6 py-3 text-right font-mono font-bold ${(b.pending || 0) > 0 ? "text-[#0a0a0a]" : "text-[#4a6b53]"}`}>
                          {inr(b.pending || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Payments ledger */}
          <div className="tc-card overflow-hidden">
            <div className="p-6 border-b border-[#e5e5e0] flex items-center justify-between">
              <div>
                <p className="tc-overline">Payments ledger</p>
                <h2 className="font-['Outfit'] text-2xl font-medium mt-1">GST paid (latest first)</h2>
              </div>
            </div>
            {(!data.payments || data.payments.length === 0) ? (
              <div className="p-12 text-sm text-[#5a5a53] text-center">
                No payments recorded yet. Click <span className="font-bold text-[#0a0a0a]">Record GST payment</span> after you file a return.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[680px]" data-testid="gst-payments-table">
                  <thead className="bg-[#f4f1eb] text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">
                    <tr>
                      <th className="text-left px-6 py-3 font-bold">Date</th>
                      <th className="text-left px-6 py-3 font-bold">Period</th>
                      <th className="text-left px-6 py-3 font-bold">CIN / Challan</th>
                      <th className="text-right px-6 py-3 font-bold">Amount</th>
                      <th className="text-right px-6 py-3 font-bold">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payments.map((p) => (
                      <tr key={p.id} className="border-t border-[#e5e5e0]" data-testid={`gst-payment-${p.id}`}>
                        <td className="px-6 py-3 font-mono">{dateFmt(p.payment_date)}</td>
                        <td className="px-6 py-3 font-mono">{p.period}</td>
                        <td className="px-6 py-3 font-mono text-xs text-[#5a5a53]">{p.cin || "—"}</td>
                        <td className="px-6 py-3 text-right font-mono text-[#0a0a0a] font-bold">{inr(p.amount)}</td>
                        <td className="px-6 py-3 text-right">
                          <button onClick={() => deletePayment(p.id)}
                            data-testid={`gst-payment-delete-${p.id}`}
                            className="text-[#5a5a53] hover:text-[#d95d39] transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Record payment modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="gst-payment-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl">Record GST payment</DialogTitle>
            <DialogDescription className="text-xs text-[#5a5a53]">
              After you've filed and paid GST on the portal, log it here so we keep your YTD totals accurate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="tc-overline">Filing cadence</Label>
              <Select value={form.cadence} onValueChange={(v) => setForm({ ...form, cadence: v, period: v === "monthly" ? defaultPaymentForm().period : quarters[0] })}>
                <SelectTrigger data-testid="gst-payment-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly (regular)</SelectItem>
                  <SelectItem value="quarterly">Quarterly (QRMP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="tc-overline">Period</Label>
              {form.cadence === "monthly" ? (
                <Input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })}
                  data-testid="gst-payment-period" />
              ) : (
                <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                  <SelectTrigger data-testid="gst-payment-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {quarters.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="tc-overline">Amount (₹)</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-2.5 top-2.5 w-4 h-4 text-[#5a5a53]" />
                  <Input type="number" min="0" step="0.01" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid="gst-payment-amount" className="pl-8" placeholder="0.00" />
                </div>
              </div>
              <div>
                <Label className="tc-overline">Payment date</Label>
                <Input type="date" value={form.payment_date}
                  onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                  data-testid="gst-payment-date" />
              </div>
            </div>
            <div>
              <Label className="tc-overline">CIN / Challan no. <span className="text-[#5a5a53] normal-case">(optional)</span></Label>
              <Input value={form.cin} onChange={(e) => setForm({ ...form, cin: e.target.value })}
                data-testid="gst-payment-cin" placeholder="e.g. 21062600000XXX" />
            </div>
            <div>
              <Label className="tc-overline">Notes <span className="text-[#5a5a53] normal-case">(optional)</span></Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="gst-payment-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="gst-payment-cancel">Cancel</Button>
            <Button onClick={savePayment} disabled={saving} data-testid="gst-payment-save"
              className="bg-[#0a0a0a] hover:bg-[#1f1f1f] text-white">
              {saving ? "Saving…" : "Save payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, sub, accent = "#0a0a0a", small = false, testid }) {
  return (
    <div className="bg-white p-4 md:p-6 min-w-0" data-testid={testid}>
      <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#5a5a53] leading-tight">{label}</p>
      <p className={`font-['Outfit'] ${small ? "text-lg md:text-xl" : "text-xl md:text-2xl"} font-bold mt-2 font-mono break-words`} style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[11px] text-[#5a5a53] mt-1">{sub}</p>}
    </div>
  );
}
